import React, { useState, useEffect, useCallback } from 'react';
import { useOrg } from '../context/OrgContext';
import { usePermissions } from '../components/PermissionGuard';
import { PageHeader } from '../components/PageHeader';
import * as programRequirementsApi from '../api/programRequirements';

export function ComplianceChecklist() {
  const { selectedOrg } = useOrg();
  const { canReadAssets } = usePermissions();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProgram, setSelectedProgram] = useState('program-nevi');
  const [selectedSite, setSelectedSite] = useState('');

  const loadStatus = useCallback(async () => {
    if (!selectedOrg || !canReadAssets) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data = await programRequirementsApi.getComplianceStatus(
        selectedOrg.id,
        selectedProgram,
        { siteId: selectedSite || undefined }
      );
      setStatus(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedOrg, canReadAssets, selectedProgram, selectedSite]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  if (!canReadAssets) {
    return (
      <div style={styles.container}>
        <div style={styles.accessDenied}>
          <h2>Access Denied</h2>
          <p>You do not have permission to view compliance status.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <PageHeader
        title="Compliance Checklist"
        subtitle="Required document completeness by program"
      />

      <div style={styles.toolbar}>
        <div style={styles.filters}>
          <select
            value={selectedProgram}
            onChange={(e) => setSelectedProgram(e.target.value)}
            style={styles.select}
            data-testid="program-filter"
          >
            <option value="program-nevi">NEVI</option>
            <option value="program-fleet">Fleet</option>
          </select>
          <input
            type="text"
            placeholder="Site ID (optional)"
            value={selectedSite}
            onChange={(e) => setSelectedSite(e.target.value)}
            style={styles.input}
            data-testid="site-filter"
          />
        </div>
      </div>

      {error && (
        <div style={styles.error}>{error}</div>
      )}

      {loading ? (
        <div style={styles.loading}>Loading compliance status...</div>
      ) : status && (
        <>
          <div style={styles.completenessPanel} data-testid="completeness-panel">
            <div style={styles.completenessCircle}>
              <div style={styles.completenessValue}>{status.completenessPercent}%</div>
              <div style={styles.completenessLabel}>Complete</div>
            </div>
            <div style={styles.completenessStats}>
              <div style={styles.stat}>
                <span style={styles.statValue}>{status.totalCompleted}</span>
                <span style={styles.statLabel}>Completed</span>
              </div>
              <div style={styles.stat}>
                <span style={styles.statValue}>{status.totalRequired}</span>
                <span style={styles.statLabel}>Required</span>
              </div>
              <div style={styles.stat}>
                <span style={styles.statValue}>{status.missingItems.length}</span>
                <span style={styles.statLabel}>Missing</span>
              </div>
            </div>
          </div>

          {status.missingItems.length > 0 && (
            <div style={styles.missingPanel} data-testid="missing-panel">
              <h3 style={styles.panelTitle}>Missing Documents</h3>
              <ul style={styles.missingList}>
                {status.missingItems.map(item => (
                  <li key={item.requirementId} style={styles.missingItem}>
                    <span style={styles.missingName}>{item.name}</span>
                    <span style={styles.missingMeta}>
                      {item.category} • {item.scope} scope • {item.missing} needed
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div style={styles.requirementsPanel}>
            <h3 style={styles.panelTitle}>All Requirements</h3>
            <table style={styles.table} data-testid="requirements-table">
              <thead>
                <tr>
                  <th style={styles.th}>Requirement</th>
                  <th style={styles.th}>Category</th>
                  <th style={styles.th}>Scope</th>
                  <th style={styles.th}>Required</th>
                  <th style={styles.th}>Current</th>
                  <th style={styles.th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {status.requirements.map(req => (
                  <tr key={req.requirementId} style={styles.tr}>
                    <td style={styles.td}>{req.name}</td>
                    <td style={styles.td}>
                      <span style={styles.categoryBadge}>{req.category}</span>
                    </td>
                    <td style={styles.td}>{req.scope}</td>
                    <td style={styles.td}>{req.requiredCount}</td>
                    <td style={styles.td}>{req.currentCount}</td>
                    <td style={styles.td}>
                      {req.completed ? (
                        <span style={styles.completeBadge}>Complete</span>
                      ) : (
                        <span style={styles.incompleteBadge}>Incomplete</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

const styles = {
  container: {
    padding: '2rem',
    maxWidth: '1200px',
    margin: '0 auto'
  },
  accessDenied: {
    padding: '3rem',
    textAlign: 'center',
    background: '#fff',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.5rem'
  },
  filters: {
    display: 'flex',
    gap: '0.5rem'
  },
  select: {
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    fontSize: '0.875rem',
    background: 'white'
  },
  input: {
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    fontSize: '0.875rem'
  },
  error: {
    padding: '1rem',
    background: '#fed7d7',
    color: '#c53030',
    borderRadius: '8px',
    marginBottom: '1rem'
  },
  loading: {
    padding: '2rem',
    textAlign: 'center',
    color: '#718096'
  },
  completenessPanel: {
    display: 'flex',
    alignItems: 'center',
    gap: '2rem',
    background: 'white',
    borderRadius: '8px',
    padding: '2rem',
    marginBottom: '1.5rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  completenessCircle: {
    width: '120px',
    height: '120px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white'
  },
  completenessValue: {
    fontSize: '2rem',
    fontWeight: '700'
  },
  completenessLabel: {
    fontSize: '0.75rem',
    opacity: 0.9
  },
  completenessStats: {
    display: 'flex',
    gap: '2rem'
  },
  stat: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  },
  statValue: {
    fontSize: '1.5rem',
    fontWeight: '600',
    color: '#2d3748'
  },
  statLabel: {
    fontSize: '0.75rem',
    color: '#718096'
  },
  missingPanel: {
    background: '#fffaf0',
    borderRadius: '8px',
    padding: '1.5rem',
    marginBottom: '1.5rem',
    border: '1px solid #fbd38d'
  },
  panelTitle: {
    margin: '0 0 1rem 0',
    fontSize: '1rem',
    fontWeight: '600',
    color: '#2d3748'
  },
  missingList: {
    listStyle: 'none',
    padding: 0,
    margin: 0
  },
  missingItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.75rem 0',
    borderBottom: '1px solid #fbd38d'
  },
  missingName: {
    fontWeight: '500',
    color: '#c05621'
  },
  missingMeta: {
    fontSize: '0.75rem',
    color: '#975a16'
  },
  requirementsPanel: {
    background: 'white',
    borderRadius: '8px',
    padding: '1.5rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse'
  },
  th: {
    textAlign: 'left',
    padding: '0.75rem',
    background: '#f7fafc',
    borderBottom: '1px solid #e2e8f0',
    fontWeight: '600',
    fontSize: '0.875rem',
    color: '#4a5568'
  },
  tr: {
    borderBottom: '1px solid #e2e8f0'
  },
  td: {
    padding: '0.75rem',
    fontSize: '0.875rem'
  },
  categoryBadge: {
    padding: '0.25rem 0.5rem',
    background: '#e2e8f0',
    borderRadius: '4px',
    fontSize: '0.75rem',
    textTransform: 'capitalize'
  },
  completeBadge: {
    padding: '0.25rem 0.5rem',
    background: '#c6f6d5',
    color: '#276749',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: '500'
  },
  incompleteBadge: {
    padding: '0.25rem 0.5rem',
    background: '#fed7d7',
    color: '#c53030',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: '500'
  }
};
